import { useEffect } from 'react';
import dayjs from 'dayjs';
import GlobalStyles from '@mui/material/GlobalStyles';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { convertToDateObject, convertToISOString } from 'utils/helpers';

interface TimePickerTypes {
    value?: string | Date;
    name?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    setValue?: (value: any) => void;
    disabled?: boolean;
    defaultNull?: boolean;
    label?: string;
}

export default function TimePickerValue({ value, setValue, disabled, defaultNull, label }: TimePickerTypes) {
    const formattedValue = defaultNull ? null : dayjs(value ? convertToDateObject(value) : null);
    const CLOCK_COLOR = '#0A7AFF';

    useEffect(() => {
        if (!value && !defaultNull) {
            const date = convertToISOString(new Date());
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- pre-existing short-circuit-as-conditional idiom
            setValue && setValue(date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- MOUNT ONLY by product decision (Option A). Initial seed when empty and defaultNull is false. Do NOT add `value`: clearing would re-seed "now" and make empty impossible. Do NOT add `setValue`/`defaultNull` either — those would also re-seed after mount. See docs/react-hooks-cleanup/07-phase7-date-time-picker-decision.md
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const handleTimeChange = (time: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- pre-existing short-circuit-as-conditional idiom
        setValue && setValue(time);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/*
             * The AM/PM column only ever has 2 rows, but MUI pads it with a trailing ::after
             * spacer (~186px, sized for the 12-row hours/minutes columns) so the last item can be
             * scrolled to a centered position, then auto-scrolls there on open. For a 2-item list
             * that scroll lands near its maximum, pushing the OTHER option almost entirely above
             * the visible area (it looks unreachable, though it isn't — confirmed by scrolling it
             * back and clicking). Zeroing that spacer makes the column's content shorter than its
             * box, so the browser can't scroll it at all and both AM and PM just stay visible
             * together. This popup is rendered in a portal outside this component's own DOM
             * subtree, so a plain `sx` on the picker never reaches it — GlobalStyles is the
             * supported way to reach it. Scoped by aria-label plus the picker's own stable class,
             * so it can't affect hours/minutes here or any other digital clock in the app.
             */}
            <GlobalStyles
                styles={{
                    '.MuiMultiSectionDigitalClockSection-root[aria-label="Select meridiem"]::after': {
                        height: 0,
                    },
                }}
            />
            <DemoContainer components={['TimePicker', 'TimePicker']}>
                <TimePicker
                    slots={{
                        openPickerIcon: (props) => <AccessTimeIcon {...props} sx={{ color: CLOCK_COLOR }} />,
                    }}
                    label={label || 'Time'}
                    value={formattedValue}
                    onChange={handleTimeChange}
                    disabled={disabled}
                />
            </DemoContainer>
        </LocalizationProvider>
    );
}
